import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import SelectBox from "@/components/ui/SelectBox";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Table, { TableColumn } from "@/components/ui/Table";
import { CheckBox } from "@/components/ui/CheckBox";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { useUpdateCompanyId, useUserActiveList } from "@/hooks/member/useMember";
import { useUserMenuList, useSaveMenuPermission } from "@/hooks/menu/useMenu";
import { Member } from "@/interface/member";
import { MemberMenuPermission } from "@/interface/menu";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import WorkPsermissionsModal from "@/components/modal/setting/se_company/WorkPsermissionsModal";

export default function WorkPermissionsPage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState<string>("all"); // all, active, inactive
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<Member[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableVer, setTableVer] = useState(0);
  const [rightTableVer, setRightTableVer] = useState(0);
  const [selectedLeftRow, setSelectedLeftRow] = useState<Member | null>(null);
  const [userMenuPermissions, setUserMenuPermissions] = useState<MemberMenuPermission[]>([]);
  const [searchTrigger, setSearchTrigger] = useState(0); // 강제 조회 트리거
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const menuTableScrollRef = useRef<number>(0);
  const menuTableContainerRef = useRef<HTMLDivElement | null>(null);

  // isActive 파라미터 계산: "all" -> null, "active" -> true, "inactive" -> false
  const isActiveParam = isActiveFilter === "all" ? null : isActiveFilter === "active" ? true : false;

  // 사원 목록 조회 (재직 상태 필터링 포함)
  const { data: userList, isLoading } = useUserActiveList({
    page: currentPage,
    size: 20,
    search: actualSearchValue,
    sort: "id,desc",
    isActive: isActiveParam,
    name: actualSearchValue,
    trigger: searchTrigger,
  });

  const { data: companyList } = useCodeListByCompanyCode();
  const updateCompanyIdMutation = useUpdateCompanyId();
  const userMenuListMutation = useUserMenuList();
  const savePermissionMutation = useSaveMenuPermission();

  // 검색어, 필터, 또는 검색 트리거 변경시 초기화
  useEffect(() => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setLoadingMore(false);
  }, [actualSearchValue, isActiveFilter, searchTrigger]);

  // 데이터 누적 로직
  useEffect(() => {
    if (userList && userList.content && userList.content.length > 0) {
      if (currentPage === 0) {
        setAllData(userList.content);
        setTotalElements(userList.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...userList.content]);
      }
    } else if (userList && (!userList.content || userList.content.length === 0)) {
      setAllData([]);
      setTotalElements(0);
    }
  }, [userList, currentPage]);

  const hasMore = useMemo(() => {
    if (!userList) return false;
    return !userList.last && allData.length < totalElements;
  }, [userList, allData.length, totalElements]);

  useEffect(() => {
    if (userList) {
      setLoadingMore(false);
    }
  }, [userList]);

  // 무한 스크롤 hooks
  const employeeInfiniteScroll = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async () => {
      if (hasMore) {
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const companyPermissionInfiniteScroll = useInfiniteScrollPage({
    data: selectedLeftRow ? companyList || [] : [],
    initialLoadCount: 5,
    loadMoreCount: 5,
  });

  const menuPermissionInfiniteScroll = useInfiniteScrollPage({
    data: selectedLeftRow ? userMenuPermissions : [],
    initialLoadCount: 8,
    loadMoreCount: 8,
  });

  const handleReset = useCallback(() => {
    setSearchValue("");
    setActualSearchValue("");
    setIsActiveFilter("all");
    setSearchTrigger(prev => prev + 1); // 강제 재조회 트리거
    setSelectedLeftRow(null);
    setUserMenuPermissions([]);
    setTableVer(prev => prev + 1);
    setRightTableVer(prev => prev + 1);
  }, []);

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchValue);
    setSearchTrigger(prev => prev + 1); // 강제 재조회 트리거
    setTableVer(prev => prev + 1);
  }, [searchValue]);

  const handlerLeftRowClick = useCallback(
    (row: Member) => {
      setSelectedLeftRow(row);

      // 사원 선택 시 메뉴 권한 데이터 가져오기
      userMenuListMutation.mutate(
        { name: row.name },
        {
          onSuccess: data => {
            setUserMenuPermissions(data || []);
            // 오른쪽 테이블 리렌더링
            setRightTableVer(prev => prev + 1);
          },
          onError: error => {
            setUserMenuPermissions([]);
            // 오른쪽 테이블 리렌더링
            setRightTableVer(prev => prev + 1);
          },
        }
      );
    },
    [userMenuListMutation]
  );

  const handleCompanyChange = useCallback(
    (companyId: number, checked: boolean) => {
      if (!selectedLeftRow) return;

      // 현재 companyId를 배열로 변환 (쉼표로 구분된 문자열)
      const currentCompanyIds = selectedLeftRow.companyId
        ? String(selectedLeftRow.companyId)
            .split(",")
            .map(id => Number(id.trim()))
            .filter(id => !isNaN(id))
        : [];

      // 체크/해제에 따라 배열 업데이트
      let updatedCompanyIds: number[];
      if (checked) {
        // 체크: 배열에 추가 (중복 체크)
        if (!currentCompanyIds.includes(companyId)) {
          updatedCompanyIds = [...currentCompanyIds, companyId];
        } else {
          updatedCompanyIds = currentCompanyIds;
        }
      } else {
        // 체크 해제: 배열에서 제거
        updatedCompanyIds = currentCompanyIds.filter(id => id !== companyId);
      }

      // 배열을 쉼표로 구분된 문자열로 변환
      const newCompanyIdString = updatedCompanyIds.join(",");

      updateCompanyIdMutation.mutate(
        {
          name: selectedLeftRow.name,
          companyId: newCompanyIdString,
        },
        {
          onSuccess: () => {
            // 성공 시 선택된 사원의 companyId 업데이트
            setSelectedLeftRow(prev => (prev ? { ...prev, companyId: newCompanyIdString } : null));
          },
        }
      );
    },
    [selectedLeftRow, updateCompanyIdMutation]
  );

  const handlePermissionChange = useCallback(
    (menuId: number, permissionType: string, checked: boolean) => {
      if (!selectedLeftRow) return;

      // 현재 메뉴의 권한 상태 찾기
      const currentMenu = userMenuPermissions.find(menu => menu.menuId === menuId);
      if (!currentMenu) return;

      let permissions: Record<string, boolean>;

      if (permissionType === "allAllowed") {
        // "전체" 체크박스: 모든 권한을 체크/해제
        permissions = {
          allAllowed: checked,
          canRead: checked,
          canCreate: checked,
          canUpdate: checked,
          canDelete: checked,
          canSave: checked,
          canPrint: checked,
          canExport: checked,
        };

        // UI 즉시 업데이트
        setUserMenuPermissions(prev =>
          prev.map(menu =>
            menu.menuId === menuId
              ? {
                  ...menu,
                  allAllowed: checked,
                  canRead: checked,
                  canCreate: checked,
                  canUpdate: checked,
                  canDelete: checked,
                  canSave: checked,
                  canPrint: checked,
                  canExport: checked,
                }
              : menu
          )
        );
      } else {
        // 개별 권한: 기존 권한 + 변경된 권한 모두 포함
        permissions = {
          allAllowed: currentMenu.allAllowed,
          canRead: permissionType === "canRead" ? checked : currentMenu.canRead,
          canCreate: permissionType === "canCreate" ? checked : currentMenu.canCreate,
          canUpdate: permissionType === "canUpdate" ? checked : currentMenu.canUpdate,
          canDelete: permissionType === "canDelete" ? checked : currentMenu.canDelete,
          canSave: permissionType === "canSave" ? checked : currentMenu.canSave,
          canPrint: permissionType === "canPrint" ? checked : currentMenu.canPrint,
          canExport: permissionType === "canExport" ? checked : currentMenu.canExport,
        };

        // UI 즉시 업데이트
        setUserMenuPermissions(prev =>
          prev.map(menu =>
            menu.menuId === menuId
              ? {
                  ...menu,
                  [permissionType]: checked,
                }
              : menu
          )
        );
      }

      // 스크롤 위치 저장
      if (menuTableContainerRef.current) {
        const scrollContainer = menuTableContainerRef.current.querySelector('div[style*="max-height"]');
        if (scrollContainer) {
          menuTableScrollRef.current = scrollContainer.scrollTop;
        }
      }

      savePermissionMutation.mutate(
        {
          memberName: selectedLeftRow.name,
          menuId: menuId,
          permissions: permissions,
        },
        {
          onSuccess: () => {
            // 성공 시 서버에서 최신 데이터 재조회 (혹시 모를 동기화 문제 방지)
            userMenuListMutation.mutate(
              { name: selectedLeftRow.name },
              {
                onSuccess: data => {
                  setUserMenuPermissions(data || []);
                  setRightTableVer(prev => prev + 1);
                  // 스크롤 위치 복원
                  setTimeout(() => {
                    if (menuTableContainerRef.current) {
                      const scrollContainer = menuTableContainerRef.current.querySelector('div[style*="max-height"]');
                      if (scrollContainer) {
                        scrollContainer.scrollTop = menuTableScrollRef.current;
                      }
                    }
                  }, 50);
                },
              }
            );
          },
          onError: () => {
            // 실패 시 원래 상태로 복구
            userMenuListMutation.mutate(
              { name: selectedLeftRow.name },
              {
                onSuccess: data => {
                  setUserMenuPermissions(data || []);
                  setRightTableVer(prev => prev + 1);
                  // 스크롤 위치 복원
                  setTimeout(() => {
                    if (menuTableContainerRef.current) {
                      const scrollContainer = menuTableContainerRef.current.querySelector('div[style*="max-height"]');
                      if (scrollContainer) {
                        scrollContainer.scrollTop = menuTableScrollRef.current;
                      }
                    }
                  }, 50);
                },
              }
            );
          },
        }
      );
    },
    [selectedLeftRow, savePermissionMutation, userMenuListMutation, userMenuPermissions]
  );

  // 테이블 컬럼 정의
  const employeeColumns: TableColumn[] = [
    {
      key: "__no",
      header: "번호",
      group: "사원 목록",
      render: (value: any, row: any, index: number) => index + 1,
    },
    { key: "name", header: "사원명", group: "사원 목록" },
    {
      key: "isActive",
      header: "재직여부",
      render: (value: boolean) => (value ? "재직" : "퇴직"),
      group: "사원 목록",
    },
  ];

  const companyPermissionColumns: TableColumn[] = [
    {
      key: "__no",
      header: "번호",
      render: (value: any, row: any, index: number) => index + 1,
    },
    { key: "name", header: "회사명" },
    {
      key: "useYn",
      header: "사용",
      render: (value: string, row: any) => {
        // 선택된 사원의 companyId 문자열을 배열로 변환하여 현재 회사 ID가 포함되어 있는지 확인
        const companyIds = selectedLeftRow?.companyId
          ? String(selectedLeftRow.companyId)
              .split(",")
              .map(id => Number(id.trim()))
              .filter(id => !isNaN(id))
          : [];
        const isChecked = companyIds.includes(row.codeId);

        return (
          <CheckBox
            checked={isChecked}
            onChange={e => {
              // 체크/해제 모두 처리
              handleCompanyChange(row.codeId, e.target.checked);
            }}
          />
        );
      },
    },
  ];

  const menuPermissionColumns: TableColumn[] = [
    {
      key: "__no",
      header: "번호",
      render: (value: any, row: any, index: number) => index + 1,
      group: "메뉴 권한",
    },
    {
      key: "parentName",
      header: "상위메뉴",
      group: "메뉴 권한",
    },
    {
      key: "name",
      header: "하위메뉴",
      group: "메뉴 권한",
    },
    {
      key: "allAllowed",
      header: "전체",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "allAllowed", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canRead",
      header: "조회",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canRead", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canCreate",
      header: "추가",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canCreate", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canUpdate",
      header: "수정",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canUpdate", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canDelete",
      header: "삭제",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canDelete", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canSave",
      header: "저장",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canSave", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canPrint",
      header: "출력",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canPrint", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
    {
      key: "canExport",
      header: "엑셀",
      render: (value: boolean, row: any) => (
        <CheckBox checked={value} onChange={e => handlePermissionChange(row.menuId, "canExport", e.target.checked)} />
      ),
      group: "메뉴 권한",
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <SelectBox
            label="재직여부"
            value={isActiveFilter}
            onChange={(value: string) => setIsActiveFilter(value)}
            options={[
              { value: "all", label: "전체" },
              { value: "active", label: "재직" },
              { value: "inactive", label: "퇴직" },
            ]}
            className="w-35 z-9998"
          />
          <InputBox
            label="검색어"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-end mb-2">
          <div className="flex gap-2">
            <PermissionButton permission="canCreate" variant="primary" onClick={() => setIsPermissionModalOpen(true)}>
              권한복사
            </PermissionButton>
            <PermissionButton permission="canCreate" variant="etc">
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc">
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc">
              삭제
            </PermissionButton>
          </div>
        </div>

        {/* 테이블 레이아웃: 왼쪽 1/3, 오른쪽 2/3 */}
        <div className="flex flex-1 min-h-0 gap-4">
          {/* 왼쪽 테이블 - 사원 목록 */}
          <div className="w-1/3 flex flex-col">
            <div className="mb-2">
              <CountBox count={totalElements} />
            </div>
            <Table
              key={`employee-table-v${tableVer}`}
              {...employeeInfiniteScroll.tableProps}
              columns={employeeColumns}
              data={allData}
              resizable
              highlightOnClick
              className="h-full"
              maxHeight="calc(100vh - 300px)"
              loadingMore={loadingMore}
              onRowClick={handlerLeftRowClick}
            />
          </div>

          {/* 오른쪽 테이블들 */}
          <div className="w-2/3 flex flex-col gap-4">
            {/* 회사 권한 섹션 */}
            <div className="h-1/3 flex flex-col">
              <div className="mb-2">
                <CountBox count={selectedLeftRow ? companyList?.length || 0 : 0} />
              </div>
              <Table
                key={`company-table-v${rightTableVer}-${companyPermissionInfiniteScroll.tableKey}`}
                {...companyPermissionInfiniteScroll.tableProps}
                columns={companyPermissionColumns}
                data={selectedLeftRow ? companyList || [] : []}
                resizable
                className="h-full"
                maxHeight="200px"
              />
            </div>

            {/* 메뉴 권한 섹션 */}
            <div className="h-2/3 flex flex-col" ref={menuTableContainerRef}>
              <Table
                key={`menu-table-v${rightTableVer}-${menuPermissionInfiniteScroll.tableKey}`}
                {...menuPermissionInfiniteScroll.tableProps}
                columns={menuPermissionColumns}
                data={selectedLeftRow ? userMenuPermissions : []}
                resizable
                className="h-full"
                maxHeight="calc(100vh - 550px)"
              />
            </div>
          </div>
        </div>
      </main>

      {/* 권한 복사 모달 */}
      <WorkPsermissionsModal
        isOpen={isPermissionModalOpen}
        onClose={() => setIsPermissionModalOpen(false)}
        mode="copy"
        isActiveFilter={isActiveFilter}
      />
    </div>
  );
}
